import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, Text, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { useCallback, useEffect } from 'react';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { getBoysenberryPath } from '../../../../routes/utils';
import { deserializeAdditionalInfo } from '../../utils/additional-info-json';
import { usePageTitle } from '../../utils/use-page-title';
import { useRetry } from '../hooks/use-retry';

import type { flowScreen_loadFlowQuery } from './__generated__/flowScreen_loadFlowQuery.graphql';
import { IframePlaceholder } from './iframe-placeholder';
import { PipelineFrame } from './pipeline-frame';

type ScreenProps = {
	flowId: string | null;
	queryRef: PreloadedQuery<flowScreen_loadFlowQuery>;
	refreshQuery: () => void;
};

const MAX_REQUEST_COUNT = 600;

type ScreenState = 'INITIALIZING' | 'IFRAME_STARTING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

const getScreenState = (
	flow: flowScreen_loadFlowQuery['response']['devai_flowSessionGetByIDAndCloudID'],
): ScreenState => {
	switch (flow?.status ?? null) {
		case null:
		case 'PENDING':
			return 'INITIALIZING';
		case 'COMPLETED':
			return 'COMPLETED';
		case 'FAILED':
			return 'FAILED';
		default:
			break;
	}

	switch (flow?.pipelines?.[0]?.status ?? null) {
		default:
		case null:
		case 'PROVISIONED':
			return 'INITIALIZING';
		case 'STARTING':
			return 'IFRAME_STARTING';
		case 'IN_PROGRESS':
			return 'IN_PROGRESS';
		case 'STOPPED':
			return 'COMPLETED';
		case 'FAILED':
			return 'FAILED';
	}
};

export const FlowScreen = ({ flowId, queryRef, refreshQuery }: ScreenProps) => {
	const data = usePreloadedQuery<flowScreen_loadFlowQuery>(
		graphql`
			query flowScreen_loadFlowQuery($sessionId: ID!, $cloudId: ID!) {
				devai_flowSessionGetByIDAndCloudID(sessionId: $sessionId, cloudId: $cloudId)
					@optIn(to: "DevAiFlowSessionGetByIDAndCloudID") {
					id
					createdAt
					status
					additionalInfoJSON
					pipelines {
						status
						url
					}
				}
			}
		`,
		queryRef,
	);

	const flow = data?.devai_flowSessionGetByIDAndCloudID;
	const url = flow?.pipelines?.[0]?.url;
	const screenState = getScreenState(flow);

	const { branchName } = deserializeAdditionalInfo(flow?.additionalInfoJSON ?? '{}');
	usePageTitle(`${branchName} - Flow`);

	const refreshFlowData = useCallback(() => {
		if (flowId && (screenState === 'INITIALIZING' || screenState === 'IFRAME_STARTING')) {
			refreshQuery();
		}
	}, [flowId, refreshQuery, screenState]);

	const { stopRetry, hasExceededMaxRequests } = useRetry(refreshFlowData, 1000, MAX_REQUEST_COUNT);

	useEffect(() => {
		if (screenState === 'IN_PROGRESS') {
			stopRetry();
		}
	}, [stopRetry, screenState]);

	const showIntroText = () => {
		if (hasExceededMaxRequests) {
			return (
				<Text size="small" color="color.text.danger">
					Flow took longer than usual to start... Please refresh the page to try again.
				</Text>
			);
		}

		return <Text size="medium">Preparing your flow... This can take a few minutes.</Text>;
	};

	const showLoadingScreen = () => {
		return (
			<>
				{showIntroText()}
				<IframePlaceholder />
			</>
		);
	};

	const showEndScreen = () => {
		return (
			<SectionMessage
				appearance="information"
				actions={
					<SectionMessageAction>
						<Link href={getBoysenberryPath()}>Return to home</Link>
					</SectionMessageAction>
				}
			>
				<Text>
					This flow has{' '}
					<Lozenge appearance={screenState === 'FAILED' ? 'removed' : 'success'}>
						{screenState}
					</Lozenge>{' '}
					and it's not available anymore.
				</Text>
			</SectionMessage>
		);
	};

	return (
		<Box xcss={screenState === 'IN_PROGRESS' ? fullHeightStyles : flexContainerStyles}>
			{(screenState === 'INITIALIZING' || screenState === 'IFRAME_STARTING') && showLoadingScreen()}
			{(screenState === 'COMPLETED' || screenState === 'FAILED') && showEndScreen()}
			{(screenState === 'IFRAME_STARTING' || screenState === 'IN_PROGRESS') && (
				<PipelineFrame url={url ?? ''} isFullScreen={screenState === 'IN_PROGRESS'} />
			)}
		</Box>
	);
};

const fullHeightStyles = xcss({
	display: 'grid',
	width: '100%',
	height: '100%',
});

const flexContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
	gap: 'space.400',
	justifyContent: 'center',
	alignItems: 'center',
});
